/* Other Ops — split by who can act (Ale ruling 2026-08-09).
 *
 * OFFICER (`<OtherOpsSurface/>`): the SYSTEM FAILURES line. That line is
 * not a dead count — `document_stuck` and `dead_letter_review` rows each
 * carry a one-click WHOLE-GROUP Retry the officer may press; `unroutable`
 * rows carry no verb at all and read "admin only". The daily-digest
 * acknowledgement card left this view on 2026-09-04 (audit cut 1, a UI
 * removal only): the digest rows keep minting and closing server-side;
 * the day's unchanged documents are a state on the Documents page.
 *
 * ADMIN (`<SystemFailuresAdminView/>`): the full diagnosis cards — the
 * machine's own error, the per-leg PARTIAL retry, discard, and the raw
 * payload of an unrouted type.
 *
 * Honest reporting binds on both: a retry that dead-letters AGAIN says
 * so and the work stays live (never-dead-end, Ale 2026-07-22).
 *
 * NOT HERE, by ruling: `request_unlocked_copy` — a state of a DOCUMENT
 * on the Documents page, not an ops row and not a chase row (a chase row
 * is an open obligation; an unreadable document has none). Parked
 * orphans and byte duplicates, same reason.
 *
 * Spec of record: ops-design-v1-2026-08-09.html.
 *
 * ── W6 SEAM ──────────────────────────────────────────────────────────
 * The workbench shell owns the four surface lines, the reusable queue
 * component and all routing. This module exports the OPS surface's own
 * rendering only:
 *
 *   <OtherOpsSurface fundId onOpenAdminView onCountsChange />
 *       the officer stream OTHER OPS's Enter lands on — the counts line
 *       and the failures line, no cards.
 *   <SystemFailureRow row />            (./SystemFailureRow)
 *       ONE row of the system-failures line, owning the whole-group
 *       Retry verb and its honest outcome text. Drop it into W6's line.
 *   useOpsCounts(fundId)                (./api)
 *       `officer_total` (the leading number) · `digest` ·
 *       `system_failures` (the "(admin)" qualifier) · per-kind
 *       `stuck` / `dead_letter` / `unroutable`.
 *   <SystemFailuresAdminView fundId />
 *       the admin view the line links to.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import OpsCard from './OpsCard'
import SystemFailureRow from './SystemFailureRow'
import { HelpDot } from '../guidance/Guidance'
import { EMPTY_OPS_COUNTS, fetchOpsCards, useOpsSurfaceEnabled } from './api'
import { EmptyState, ErrorState, Skeleton } from '../state'
import type { OpsAudience, OpsCardsResponse, OpsCounts } from './types'
import './ops.css'

interface Props {
  fundId?: string | null
  /** Optional: hand a failure row to the admin view. W6 owns routing, so
   *  without it the row simply reads "admin only". */
  onOpenAdminView?: (taskId: string) => void
  onCountsChange?: (counts: OpsCounts) => void
}

function useOpsPayload(fundId: string | null | undefined, audience: OpsAudience) {
  const [payload, setPayload] = useState<OpsCardsResponse | null>(null)
  const [error, setError] = useState<unknown>(null)

  const load = useCallback(async () => {
    try {
      setPayload(await fetchOpsCards(fundId, audience))
      setError(null)
    } catch (err: unknown) {
      setError(err)
    }
  }, [fundId, audience])

  useEffect(() => {
    void load()
  }, [load])

  return { payload, error, reload: load }
}

/* ── officer ─────────────────────────────────────────────────────────── */

export function OtherOpsSurface({ fundId, onOpenAdminView, onCountsChange }: Props) {
  const { payload, error, reload } = useOpsPayload(fundId, 'officer')

  const counts = payload?.counts ?? EMPTY_OPS_COUNTS
  useEffect(() => {
    if (payload) onCountsChange?.(payload.counts)
  }, [payload, onCountsChange])

  const rows = useMemo(() => payload?.system_failures ?? [], [payload])

  return (
    <section className="otherops" data-testid="other-ops-surface">
      <div className="inner">
        <h1>Other Ops</h1>
        <p className="sub">
          What the machine could not land. Everything else that used to live here is a state of a
          document, not a task.
        </p>

        {error ? (
          <ErrorState what="digest" error={error} onRetry={reload} testId="ops-load-error" />
        ) : null}

        {/* The officer's counts: the failures, and the day's documents that
            processed and changed nothing — a Documents state, not a task
            (audit cut 1, 2026-09-04). `officer_total` still arrives from
            the endpoint; it counts the unseen digest rows and is not
            printed. */}
        <div className="qbar" data-testid="ops-counts">
          {counts.system_failures} system failures (admin) · {counts.processed_unchanged} processed,
          unchanged —{' '}
          <a href="/v2/documents" data-testid="ops-documents-link">
            see Documents ›
          </a>
        </div>

        {/* NEVER HIDDEN. The failures line renders at zero, at every
            count, under every fund filter — Ale's never-dead-end ruling
            (2026-07-22): the visibility line "is also a failure", and
            hiding it rebuilds the silent dead end one level up. */}
        <section className="sfline" data-testid="ops-system-failures">
          <h2>
            System failures <span className="qual">· admin</span> <HelpDot k="ops.failure" />
          </h2>
          <p className="sub">
            The machine did not land these. Retry runs the whole thing again and tells you what
            actually happened; anything finer — which leg was poison, an unrouted type — is admin
            work.
          </p>
          {rows.length > 0 ? (
            rows.map((row) => (
              <SystemFailureRow
                key={row.task_id}
                row={row}
                onSettled={reload}
                onOpenAdminView={onOpenAdminView}
              />
            ))
          ) : payload || error ? (
            <p className="sub none" data-testid="ops-system-failures-none">
              Nothing failed to land.
            </p>
          ) : (
            <p className="sub">loading…</p>
          )}
        </section>

        <p className="foot">
          Not here by design: an unreadable upload, a parked orphan and a byte-duplicate are states
          of a <b>document</b>, on the Documents page. An orphan re-enters the proposal decks by
          itself once its missing piece exists; an unreadable file is asked about from the document,
          and the request it mints shows up in Chase.
        </p>
      </div>
    </section>
  )
}

/* ── admin ───────────────────────────────────────────────────────────── */

/** The diagnosis view the system-failures line links to. Full card
 *  grammar: the machine's error, the per-leg PARTIAL retry, discard, and
 *  the raw payload of an unrouted type. Admin role only — the endpoint
 *  403s anyone else. */
export function SystemFailuresAdminView({ fundId }: { fundId?: string | null }) {
  const { payload, error, reload } = useOpsPayload(fundId, 'admin')
  const [slims, setSlims] = useState<Record<string, string[]>>({})
  const cards = useMemo(() => payload?.cards ?? [], [payload])

  return (
    <section className="otherops" data-testid="ops-admin-view">
      <div className="inner">
        <h1>System failures</h1>
        <p className="sub">
          What the machine could not land, and why. Diagnosis, the per-leg retry, and the raw
          payload of anything no queue knows how to route.
        </p>

        {error ? (
          <ErrorState what="failures" error={error} onRetry={reload} testId="ops-admin-error" />
        ) : null}

        {cards.length > 0 ? (
          cards.map((card) => (
            <div key={card.card_id}>
              <OpsCard
                card={card}
                onDone={(text) => {
                  setSlims((prev) => ({
                    ...prev,
                    [card.card_id]: [...(prev[card.card_id] ?? []), text],
                  }))
                  void reload()
                }}
              />
              <div className="slims" data-testid="ops-slims">
                {(slims[card.card_id] ?? []).map((text) => (
                  <div key={text} className={`slim${text.startsWith('✗') ? ' bad' : ''}`}>
                    {text}
                  </div>
                ))}
              </div>
            </div>
          ))
        ) : payload ? (
          <EmptyState
            sentence="Nothing failed."
            door={{ label: 'Other ops ›', to: '/v2/workbench/other-ops' }}
            testId="ops-admin-all-clear"
          />
        ) : error ? null : (
          <Skeleton kind="cards" n={2} height={120} />
        )}
      </div>
    </section>
  )
}

/* ── standalone routes (dark while the flag is off) ──────────────────── */

function DarkStub() {
  return (
    <section className="otherops">
      <div className="inner">
        <p className="sub">The Other Ops queue is not enabled on this deployment.</p>
      </div>
    </section>
  )
}

/** Route element for /v2/ops. W6's `/workbench/ops` stream supersedes
 *  this page at cutover; it renders <OtherOpsSurface/> directly. */
/* While the flag probes, the page area is never white (state system). */
function ProbingOps() {
  return (
    <section className="otherops">
      <div className="inner">
        <Skeleton kind="cards" n={2} height={120} />
      </div>
    </section>
  )
}

export default function OtherOpsPage() {
  const enabled = useOpsSurfaceEnabled()
  if (enabled === null) return <ProbingOps />
  if (!enabled) return <DarkStub />
  return <OtherOpsSurface />
}

/** Route element for /v2/ops/system-failures (admin). */
export function SystemFailuresAdminPage() {
  const enabled = useOpsSurfaceEnabled()
  if (enabled === null) return <ProbingOps />
  if (!enabled) return <DarkStub />
  return <SystemFailuresAdminView />
}
